import { useEffect } from "react";
import { useSeo } from "@/lib/seo";
import { getMeta } from "@/seo/pages";
import { trackPage } from "@/lib/track";
import { ABOUT_JSONLD } from "@/seo/homeJsonld";
import { AccountMenu } from "@/components/account";
import "@/styles/home.css";

// Verbatim quotes from the 99tests brochure. Headshots added later.
const TESTIMONIALS = [
  { quote: "The use of crowd concept emulating the real world delivered a unique insight of our customer's experience.", name: "Steve Coles", title: "CTO, Allianz Insurance", note: "Also an investor in 99tests" },
  { quote: "As a company, we have made great strides in improving our website quality thanks to continued crowdsourced testing with 99tests.", name: "Daniel Yates", title: "Founder, Pitchup" },
  { quote: "99tests has been an invaluable QA partner for us over the last several months, right from Day 1.", name: "Mekin Maheshwari", title: "Chief People Officer, Flipkart" },
];
const PRESS = [
  { outlet: "YourStory", title: "Feb 2011", url: "https://cwv.yourstory.com/2011/02/crowdsourced-testing-helps-software-companies-launch-products-quicker-praveen-singh-founder-ceo-99tests" },
  { outlet: "YourStory", title: "Feb 2015", url: "https://yourstory.com/2015/02/99-problems-bugs" },
  { outlet: "Inc42", title: "Jul 2015", url: "https://inc42.com/buzz/99tests-funding/" },
  { outlet: "YourStory", title: "Oct 2015", url: "https://yourstory.com/2015/10/nasscom-product-conclave-2015-startups" },
  { outlet: "eCommerceNews NZ", title: "Aug 2016", url: "https://ecommercenews.co.nz/story/want-test-your-app-bug-thon-contest-you" },
];

const STATS = [
  ["300+", "companies tested with 99tests"],
  ["127,000+", "bugs reported and verified"],
  ["20,000", "testers registered across India"],
  ["Since 2010", "running crowdsourced testing"],
];

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="/research" data-testid="nav-research">Research</a>
        <a href="/about" data-testid="nav-about">About</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <AccountMenu />
        <a className="btn sm blue" href="/pricing" data-testid="about-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br />
        <span className="small" data-testid="about-footer-identity">Founded 2010 · Super Founder Hub. UKIBC, No 9, Prestige Blue Chip Tech Park, Hosur Main Road, Dairy Colony, Adugodi, Bengaluru, Karnataka 560029, India.</span></div>
      <div>
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/">Home</a>
        <a href="/pricing">Pricing</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

export default function About() {
  const path = "/about";
  useSeo({ ...getMeta(path), path, jsonld: ABOUT_JSONLD });
  useEffect(() => { trackPage(path); }, []);

  return (
    <>
      <Header />
      <main data-testid="about-page">
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Since 2010</b><span className="dot"></span><b>Bengaluru</b><span className="dot"></span><span>Crowdsourced real-device testing</span></div>
              <h1>The human check on software <em>since 2010.</em><br />Now on what AI builds.</h1>
              <p className="lede" data-testid="about-definition">99tests is a Bengaluru company that has run crowdsourced software testing since 2010. Today 20,000 registered testers in India run teams' test cases, including AI-generated ones, on their own phones, laptops and browsers. Every result is verified before the report goes out.</p>
            </div>
          </div>
        </section>

        <section style={{ paddingTop: 0, borderTop: 0 }}>
          <div className="wrap">
            <div className="stats" data-testid="about-stats" style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
              {STATS.map(([n, l]) => (
                <div className="stat" key={l} style={{ minWidth: 180 }}><b>{n}</b><span>{l}</span></div>
              ))}
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Our story</span><h2>A decade of real-device testing.</h2></div>
            <div className="narrow" style={{ marginLeft: 0 }}>
              <p className="lede">99tests started in 2010 to answer a question automation couldn't: does this app actually work for a real person, on the phone they own, on the network they're on? We built a community of testers across India and ran crowdsourced testing for enterprise teams for over a decade.</p>
              <p>Today that same pool — 20,000 registered testers on the budget Androids, iPhones, laptops and browsers your users actually carry — executes the test cases teams generate with AI coding tools, and returns a verified report a developer can act on. No lab devices, no emulators, no stock-Android data centre.</p>
              <p>We've worked with 300+ companies and verified over 127,000 bugs. The model is deliberately simple: fixed price, paid online, a report in three business days, and no sales call.</p>
            </div>
          </div>
        </section>

        <section>
          <div className="wrap">
            <div className="who" data-testid="about-founder">
              <div>
                <span className="eyebrow">Founder</span>
                <h2>Praveen Singh, Founder.</h2>
                <p className="lede">Praveen Singh founded 99tests in 2010 to bring human judgment to software quality, and still leads it. He has spent more than a decade building 99tests' community of real-device testers across India.</p>
                <p className="muted small">IIM Bangalore alumnus · author of <i>The Compounding Company</i>.</p>
                <p><a href="https://linkedin.com/in/praveensinghv" target="_blank" rel="noopener noreferrer" data-testid="about-founder-linkedin">Praveen Singh on LinkedIn</a></p>
              </div>
              <div>
                <img src="/assets/founder-praveen-singh.webp" alt="Praveen Singh, Founder of 99tests, speaking on stage" data-testid="about-founder-photo" loading="lazy" decoding="async" style={{ width: "100%", borderRadius: 16, border: "1px solid var(--line,#e6e8f0)", display: "block" }} />
              </div>
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">How we stand behind results</span><h2>Every finding is verified.</h2></div>
            <div className="proof" data-testid="about-verification">
              <div><h4>Separation of duties</h4><p>The tester who finds a bug is never the person who grades it. Verification is a distinct step, not a rubber stamp.</p></div>
              <div><h4>Graded against a rubric</h4><p>Severity is assigned against a published rubric with a confidence score; duplicates are merged and incomplete reports are rejected before you see them.</p></div>
              <div><h4>Evidence on every bug</h4><p>Steps, expected, actual, device, OS, network and a screen recording accompany each serious finding — reproducible without asking who found it.</p></div>
              <div><h4>Paid regardless of findings</h4><p>Sessions are timeboxed and paid whether or not bugs are found, so testers report what they see, not what earns a bonus.</p></div>
            </div>
          </div>
        </section>

        {TESTIMONIALS.length > 0 && (
          <section data-testid="about-testimonials">
            <div className="wrap">
              <div className="section-head"><span className="eyebrow">In their words</span><h2>What teams say.</h2></div>
              <div className="proof">
                {TESTIMONIALS.map((t, i) => (
                  <div key={i}><p>&ldquo;{t.quote}&rdquo;</p><p className="muted small"><b>{t.name}</b> — {t.title}{t.note ? ` · ${t.note}` : ""}</p></div>
                ))}
              </div>
            </div>
          </section>
        )}

        {PRESS.length > 0 && (
          <section className="band" data-testid="about-press">
            <div className="wrap">
              <div className="section-head"><span className="eyebrow">In the press</span><h2>Mentions.</h2></div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {PRESS.map((p, i) => (
                  <a key={i} className="btn ghost" href={p.url} target="_blank" rel="noopener noreferrer">{p.outlet}: {p.title}</a>
                ))}
              </div>
            </div>
          </section>
        )}

        <section>
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Company & contact</span><h2>Who you're dealing with.</h2></div>
            <div className="narrow" data-testid="about-company" style={{ marginLeft: 0 }}>
              <p><b>Legal entity:</b> Super Founder Hub</p>
              <p><b>Registered address:</b> UKIBC, No 9, Prestige Blue Chip Tech Park, Hosur Main Road, Dairy Colony, Adugodi, Bengaluru, Karnataka 560029, India</p>
              <p><b>Email:</b> <a href="mailto:hello@99tests.com">hello@99tests.com</a> · <b>Phone:</b> <a href="tel:+919448310439">+91-94483-10439</a></p>
              <p><b>GSTIN:</b> <span className="muted">provided on invoice (to be published here)</span></p>
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Put a real person on your next release.</h2>
                <p>Pay, upload your test cases, and testers are on it within the hour. Verified report in three business days.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
